/** Changing a task after it is filed.
 *
 *  Two endpoints behind one form, because the API splits them and each writes its own
 *  history event: `PATCH /tasks/{id}` for the text, company, priority and date, and
 *  `POST /tasks/{id}/reassign` for the people. That split is right — reassign
 *  re-resolves ids and has behaviour `edit` does not — so the form issues whichever
 *  calls are needed and reports honestly if one lands and the other does not.
 *
 *  `expected_version` goes with the PATCH. Without it the compare-and-set happens
 *  inside the request, so a form left open for ten minutes overwrites whatever landed
 *  meanwhile without saying so.
 */
import { CompanyPicker, withCurrent } from '@/components/CompanyPicker'
import { PeoplePicker, type Quorum }
  from '@/components/PeoplePicker'
import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ApiError, api, type Company, type Assignee, type Task, type Workload } from '@/api/client'
import { ErrorNote, fmtDay, todayISO } from './ui'
import { SignedOffConfirm, useSignedOffGate } from './SignedOffConfirm'
import { useAuth } from '@/lib/auth'

const PRIORITIES = ['Urgent', 'Important', 'Regular']

export function EditTaskForm({ task, onDone, onCancel, onSaved }: {
  task: Task
  onDone: () => void
  onCancel: () => void
  onSaved: () => void
}) {
  const { me } = useAuth()
  const [err, setErr] = useState<unknown>(null)
  const [conflict, setConflict] = useState<Task | null>(null)
  const [partial, setPartial] = useState<string | null>(null)
  // The version of the task this form is editing against. Deliberately state and not
  // the `task` prop, for two reasons:
  //
  //  - The prop is re-derived from the ['tasks'] query, so any background refetch
  //    would quietly move `expected_version` forward onto a version the person
  //    editing never saw - silently rebasing the save that compare-and-set exists to
  //    prevent.
  //  - After a conflict, "Edit the new version" has to actually be able to save. With
  //    the version read from the prop it could not: the list has not refetched, so the
  //    next save sent the same stale version and conflicted again, with nobody having
  //    touched the task in between. The escape hatch never escaped.
  const [base, setBase] = useState<Task>(task)
  const [form, setForm] = useState({
    task: base.task,
    company: base.company,
    priority: base.priority,
    date: base.date ?? '',      // typed nullable, though every task has one
    end_date: base.end_date ?? '',
    responsible_ids: (base.people ?? [])
      .filter((x) => x.kind === 'RESPONSIBLE').map((x) => x.user_id),
    approver_ids: (base.people ?? [])
      .filter((x) => x.kind === 'APPROVER').map((x) => x.user_id),
    responsible_rule: (base.responsible_rule ?? 'ANY') as Quorum,
    approver_rule: (base.approver_rule ?? 'ANY') as Quorum,
  })
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const companies = useQuery({
    queryKey: ['companies'], queryFn: () => api.get<Company[]>('/companies'),
  })
  // Shared `['workload']` key, so the bands cost no extra request on a page that
  // already has them.
  const load = useQuery({
    queryKey: ['workload'],
    queryFn: () => api.get<Workload>('/reports/workload'),
    // Needs APPROVER. Without this the form 403s for a manager or a self-filer -
    // the same gate already on the rail, the grid and the add drawer.
    enabled: !!me?.permissions?.reports,
  })
  const people = useQuery({
    // `/people`, not `/users`: scoped to the caller's own team, and without the
    // contact details. A manager is offered exactly the people the service will let
    // them file work onto, so the picker cannot suggest a choice that is then refused.
    queryKey: ['people'], queryFn: () => api.get<Assignee[]>('/people'),
  })
  const active = (companies.data ?? []).filter((c) => c.active)
  const staff = (people.data ?? []).filter((p) => p.active)
  const approvers = staff.filter((p) => p.roles.includes('APPROVER') || p.is_owner)

  // Only what actually changed. A no-op edit writes no history server-side, but not
  // sending it at all keeps the intent obvious.
  const fieldChanges = () => {
    const out: Record<string, unknown> = {}
    if (form.task.trim() !== base.task) out.task = form.task.trim()
    if (form.company !== base.company) out.company = form.company
    if (form.priority !== base.priority) out.priority = form.priority
    if (form.date && form.date !== base.date) out.date = form.date
    // The one field here that has to be able to send an EMPTY value. Everything else
    // treats blank as "not given", which is right for a partial update and leaves no
    // way to turn a task that runs over several days back into a one-day one - so
    // `""` is that instruction, and the service reads it as such.
    if ((form.end_date || '') !== (base.end_date ?? '')) {
      out.end_date = form.end_date || ''
    }
    return out
  }
  // Set comparison, order-insensitive: reordering the same people is not a
  // reassignment, and sending one would write a history event saying nothing happened.
  const same = (a: string[], b: string[]) =>
    a.length === b.length && [...a].sort().join() === [...b].sort().join()
  const baseDoers = (base.people ?? [])
    .filter((x) => x.kind === 'RESPONSIBLE').map((x) => x.user_id)
  const baseApprovers = (base.people ?? [])
    .filter((x) => x.kind === 'APPROVER').map((x) => x.user_id)
  const peopleChanged = !same(form.responsible_ids, baseDoers)
    || !same(form.approver_ids, baseApprovers)
    || form.responsible_rule !== (base.responsible_rule ?? 'ANY')
    || form.approver_rule !== (base.approver_rule ?? 'ANY')

  // Clearing the approver on a task awaiting approval *completes* it — there is
  // nobody left to approve and leaving it pending would strand the row. Said before
  // saving, not discovered after.
  // Removing approvers can finish the task, and emptying the list is only the simplest
  // way. Under "all must approve", dropping the last undecided approver leaves a set
  // whose every member has already approved - so the quorum is met and the task is
  // done, even though approvers remain. The wording has to distinguish the two or it
  // will be wrong half the time.
  const stillPending = base.status === 'PENDING_APPROVAL'
  const keptApprovers = (base.people ?? [])
    .filter((x) => x.kind === 'APPROVER' && form.approver_ids.includes(x.user_id))
  const emptied = stillPending && baseApprovers.length > 0
    && form.approver_ids.length === 0
  const quorumNowMet = stillPending && keptApprovers.length > 0
    && (base.approver_rule ?? 'ANY') === 'ALL'
    && keptApprovers.every((x) => x.decision === 'APPROVED')
  const willComplete = emptied || quorumNowMet

  const save = useMutation({
    mutationFn: async (acknowledge: string[] = []) => {
      const fields = fieldChanges()
      let did: string[] = []
      if (Object.keys(fields).length) {
        await api.patch(
          `/tasks/${base.task_id}?expected_version=${base.row_version}`, fields)
        did.push('the details')
      }
      if (peopleChanged) {
        try {
          await api.post(`/tasks/${base.task_id}/reassign`, {
            responsible_ids: form.responsible_ids,
            approver_ids: form.approver_ids,
            responsible_rule: form.responsible_rule,
            approver_rule: form.approver_rule,
            acknowledge_closed: acknowledge,
          })
        } catch (e) {
          // The two calls are not atomic together. If the first landed, say so
          // rather than implying nothing happened.
          //
          // A sign-off is the exception: nothing has been refused, a question is
          // outstanding, and the dialog is about to replay this half - so saying
          // "the reassignment was refused" would be wrong and alarming.
          if (did.length && !(e instanceof ApiError && e.isPersonDayClosed)) {
            setPartial('The details were saved, but the reassignment was refused.')
          }
          throw e
        }
      }
      return did
    },
    onSuccess: () => { setErr(null); setPartial(null); onSaved(); onDone() },
    onError: (e) => {
      const api_e = e as ApiError
      if (api_e instanceof ApiError && api_e.isConflict) {
        // Somebody wrote to this task while the form was open. Show what it looks
        // like now instead of overwriting it.
        api.get<Task>(`/tasks/${base.task_id}`).then(setConflict).catch(() => {})
      }
      // A sign-off is a question, not a failure. The gate below is showing it, and an
      // error banner beside the dialog would say two contradictory things at once.
      if (api_e instanceof ApiError && api_e.isPersonDayClosed) return
      setErr(e)
    },
  })

  // Somebody who has signed off is still a legitimate choice, so the server asks and
  // this replays the same save once answered.
  const gate = useSignedOffGate((ack) => save.mutateAsync(ack))

  const dirty = Object.keys(fieldChanges()).length > 0 || peopleChanged
  const ready = !!form.task.trim() && !!form.company
    && form.responsible_ids.length > 0

  if (conflict) {
    return (
      <div className="px-5 py-4 border-b border-rule space-y-3">
        <ErrorNote error={{ message:
          'Somebody else changed this task while you had it open. Nothing you typed '
          + 'has been saved.' }} />
        <div className="text-[12.5px]">
          <div className="label">It now reads</div>
          <p className="font-semibold">{conflict.task}</p>
          <p className="text-ink-3 mt-0.5">
            {conflict.company} · {conflict.priority} · {conflict.responsible_person
              || 'nobody'}{conflict.approver ? ` · approver ${conflict.approver}` : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-primary" onClick={() => {
            // Start again from what is really there, keeping the drawer open. The
            // base moves too - without it the next save would send the same stale
            // version and conflict all over again.
            setBase(conflict)
            setForm({
              task: conflict.task, company: conflict.company,
              priority: conflict.priority, date: conflict.date ?? '',
              end_date: conflict.end_date ?? '',
              responsible_ids: (conflict.people ?? [])
                .filter((x) => x.kind === 'RESPONSIBLE').map((x) => x.user_id),
              approver_ids: (conflict.people ?? [])
                .filter((x) => x.kind === 'APPROVER').map((x) => x.user_id),
              responsible_rule: (conflict.responsible_rule ?? 'ANY') as Quorum,
              approver_rule: (conflict.approver_rule ?? 'ANY') as Quorum,
            })
            setConflict(null); setErr(null)
          }}>
            Edit the new version
          </button>
          <button className="btn" onClick={() => { setConflict(null); onDone() }}>
            Discard my changes
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="px-5 py-4 border-b border-rule space-y-3">
      {gate.asking && (
        <SignedOffConfirm people={gate.asking} busy={save.isPending}
                          onCancel={gate.cancel}
                          onConfirm={() => { void gate.confirm() }} />
      )}
      {err ? <ErrorNote error={err} /> : null}
      {partial ? (
        <p className="text-[12px] text-pend-ink bg-pend-tint rounded px-2.5 py-1.5">
          {partial}
        </p>
      ) : null}

      <div>
        <label className="label">What needs doing</label>
        <textarea className="w-full rounded-md border border-rule px-2.5 py-2 text-[13px]"
                  rows={2} maxLength={2000} value={form.task} autoFocus
                  onChange={(e) => set('task', e.target.value)} />
      </div>

      <div className="flex gap-2.5">
        <div className="flex-1">
          <label className="label">Company</label>
          {/* `withCurrent` keeps an archived current value on the list, marked rather
              than hidden - so an unrelated edit cannot silently move the task. */}
          <CompanyPicker companies={withCurrent(active, base.company)}
                         value={form.company ? [form.company] : []}
                         onChange={(ns) => set('company', ns[0] ?? '')}
                         onDone={() => undefined} />
        </div>
        <div className="w-36">
          <label className="label">Priority</label>
          <select className="field" value={form.priority}
                  onChange={(e) => set('priority', e.target.value)}>
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>

      <div className="flex gap-2.5">
        <div className="flex-1">
          <label className="label">Who does it</label>
          {/* The same picker the grid and the drawer use, rather than a third
              hand-rolled select. This one had its own, and seeded it from the joined
              name string - which on a two-person task matched no option at all, so the
              control rendered blank and saving could quietly collapse the task onto one
              person. */}
          <PeoplePicker staff={staff} load={load.data} kind="doer"
                        value={form.responsible_ids} rule={form.responsible_rule}
                        onChange={(ids, rule) => setForm((f) => ({
                          ...f, responsible_ids: ids, responsible_rule: rule }))}
                        onDone={() => undefined} />
        </div>
        <div className="flex-1">
          <label className="label">Who approves</label>
          <PeoplePicker staff={approvers} load={load.data} kind="approver"
                        value={form.approver_ids} rule={form.approver_rule}
                        onChange={(ids, rule) => setForm((f) => ({
                          ...f, approver_ids: ids, approver_rule: rule }))}
                        onDone={() => undefined} />
        </div>
      </div>

      <div className="flex gap-3">
        <div className="w-44">
          <label className="label">Date</label>
          {/* The date was never shown in this drawer before. `min` matches creation:
              a task cannot be moved to a day whose carry-forward has already run. */}
          <input className="field" type="date" value={form.date} min={todayISO()}
                 onChange={(e) => set('date', e.target.value)} />
        </div>
        <div className="w-44">
          <label className="label">
            Until <span className="text-ink-3 font-normal">optional</span>
          </label>
          {/* Clearing it turns the task back into an ordinary one-day one. `min` is
              its own start date, not today - shifting a whole window later is a fair
              edit, and the server checks the new end against the new start. */}
          <input className="field" type="date" value={form.end_date}
                 min={form.date || todayISO()}
                 onChange={(e) => set('end_date', e.target.value)} />
        </div>
      </div>

      {form.end_date && form.end_date >= form.date && (
        <p className="text-[12.3px] text-ink-3">
          On the list every day from <b>{fmtDay(form.date)}</b> to{' '}
          <b>{fmtDay(form.end_date)}</b>. Not carried forward or chased as late while
          the window is open. Clear this field to make it a one-day task again.
        </p>
      )}

      {willComplete && (
        <p className="text-[12px] text-pend-ink bg-pend-tint rounded px-2.5 py-1.5">
          <b>{form.approver_ids.length === 0
            ? 'Removing the approver will complete this task.'
            : 'This will complete the task.'}</b> It is waiting for
          approval, so with nobody left to approve it there is nothing to wait for.
        </p>
      )}

      <div className="flex gap-2 pt-1">
        <button className="btn btn-primary" disabled={!dirty || !ready || save.isPending}
                onClick={() => { setErr(null); void gate.attempt() }}>
          {save.isPending ? 'Saving…' : 'Save changes'}
        </button>
        <button className="btn" onClick={onCancel}>Cancel</button>
        <span className="flex-1" />
        {!ready && (
          <span className="self-center text-[11.5px] text-pend-ink">
            {!form.task.trim() ? 'needs a description'
              : !form.company ? 'needs a company' : 'needs somebody to do it'}
          </span>
        )}
      </div>
    </div>
  )
}
